/**
 * Currency formatting helpers
 */

/**
 * Map of currency codes to their symbols
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  MXN: '$',
  EUR: '€',
  GBP: '£',
  BRL: 'R$',
  COP: '$',
  ARS: '$',
  CLP: '$',
  PEN: 'S/',
  VES: 'Bs.',
  BOB: 'Bs.',
};

/**
 * Get the currency symbol for a given currency code
 */
export const getCurrencySymbol = (currencyCode?: string): string => {
  if (!currencyCode) return '$';
  return CURRENCY_SYMBOLS[currencyCode.toUpperCase()] || currencyCode;
};

/**
 * Format an amount with the appropriate currency symbol
 * @param amount - The numeric amount to format
 * @param currencyCode - The currency code (e.g., 'USD', 'MXN', 'EUR')
 * @param decimals - Number of decimal places (default: 2)
 */
export const formatCurrency = (
  amount: number,
  currencyCode?: string,
  decimals: number = 2
): string => {
  const symbol = getCurrencySymbol(currencyCode);
  const formattedAmount = amount.toFixed(decimals);

  // For most currencies, put symbol before the amount
  // For some currencies like EUR, it's common to put it after, but we'll keep it consistent
  return `${symbol} ${formattedAmount}`;
};

/**
 * Costo por unidad de stock. Con dos decimales, $0.00045 por ml se vería como $0.00 y el
 * renglón parecería no sumar; el backend guarda seis.
 */
export const formatUnitCost = (amount: number): string =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(amount);

/**
 * Format an amount using Intl.NumberFormat for proper localization
 * @param amount - The numeric amount to format
 * @param currencyCode - The currency code (e.g., 'USD', 'MXN', 'EUR')
 * @param locale - The locale to use (default: 'es-MX' for Spanish Mexico)
 */
export const formatCurrencyLocalized = (
  amount: number,
  currencyCode: string = 'MXN',
  locale: string = 'es-MX'
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    // Fallback to simple formatting if Intl fails
    return formatCurrency(amount, currencyCode);
  }
};

/**
 * Precio de suscripción: "$49", "$1,299" o "$49.50". Sin centavos cuando es entero, como se
 * anuncian los planes; con separador de miles es-MX.
 */
export const formatPlanPrice = (amount: number): string => {
  const decimals = Number.isInteger(amount) ? 0 : 2;
  return `$${amount.toLocaleString('es-MX', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};
